import { useMemo, useRef, useState } from 'react';
import {
  type PrescribingNotificationItem,
  type PrescribingNotificationKey,
  usePrescribingNotifications,
} from '@features/notifications';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import LocalPharmacyOutlinedIcon from '@mui/icons-material/LocalPharmacyOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  alpha,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Badge from '@mui/material/Badge';
import { useEventTracking } from '@services/statsig';

import { useDoseSpotLinks } from '../hooks/useDoseSpotLinks';
import { useEPrescriptionStatus } from '../store/hooks';

type RowMeta = {
  icon: typeof ErrorOutlineIcon;
  buildCopy: (count: number) => string;
  trackingType: 'transmission' | 'pending' | 'refill' | 'change';
  isUrgent: boolean;
};

const ROW_META: Record<PrescribingNotificationKey, RowMeta> = {
  transmission_error_count: {
    icon: ErrorOutlineIcon,
    buildCopy: count =>
      count === 1
        ? '1 prescription failed to send'
        : `${count} prescriptions failed to send`,
    trackingType: 'transmission',
    isUrgent: true,
  },
  pending_prescription_count: {
    // Backend doesn't expose proxy first name on the summary endpoint yet.
    // Always render the fallback copy from the locked spec.
    icon: PersonOutlineIcon,
    buildCopy: count =>
      count === 1
        ? 'Your proxy queued 1 prescription for review'
        : `Your proxy queued ${count} prescriptions for review`,
    trackingType: 'pending',
    isUrgent: false,
  },
  refill_request_count: {
    icon: LocalPharmacyOutlinedIcon,
    buildCopy: count =>
      count === 1
        ? '1 refill request from pharmacies'
        : `${count} refill requests from pharmacies`,
    trackingType: 'refill',
    isUrgent: false,
  },
  change_request_count: {
    icon: SwapHorizIcon,
    buildCopy: count =>
      count === 1
        ? '1 prescription change request from pharmacies'
        : `${count} prescription change requests from pharmacies`,
    trackingType: 'change',
    isUrgent: false,
  },
};

export function PrescribingNotificationsBell() {
  const theme = useTheme();
  const { isUserConnected } = useEPrescriptionStatus();
  const { items, totalCount, transmissionFailureCount } = usePrescribingNotifications({
    skip: !isUserConnected,
  });
  const { getClinicianHref } = useDoseSpotLinks('prescribing_notifications');
  const { prescribingNotifications } = useEventTracking();

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const counts = useMemo(() => {
    const lookup: Record<PrescribingNotificationKey, number> = {
      transmission_error_count: 0,
      pending_prescription_count: 0,
      refill_request_count: 0,
      change_request_count: 0,
    };
    for (const item of items) {
      lookup[item.key] = item.count;
    }
    return lookup;
  }, [items]);

  if (!isUserConnected || totalCount === 0) {
    return null;
  }

  const hasTransmissionFailure = transmissionFailureCount > 0;

  const handleBellClick = () => {
    setOpen(true);
    prescribingNotifications.bellOpened({
      total_count: totalCount,
      transmission_count: counts.transmission_error_count,
      pending_count: counts.pending_prescription_count,
      refill_count: counts.refill_request_count,
      change_count: counts.change_request_count,
    });
  };

  const handleRowClick = (item: PrescribingNotificationItem) => {
    const meta = ROW_META[item.key];
    prescribingNotifications.rowClicked({
      notification_type: meta.trackingType,
      count: item.count,
    });
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="Prescribing notifications" placement="bottom" arrow>
        <IconButton
          ref={anchorRef}
          onClick={handleBellClick}
          aria-label={`Prescribing notifications, ${totalCount} ${totalCount === 1 ? 'item' : 'items'} need you`}
          size="small"
          sx={{ mx: 1 }}
        >
          <Badge
            badgeContent={totalCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                height: 18,
                minWidth: 18,
              },
            }}
          >
            {hasTransmissionFailure ? (
              <NotificationsActiveOutlinedIcon />
            ) : (
              <NotificationsOutlinedIcon />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, mt: 1 } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Prescribing
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {totalCount} {totalCount === 1 ? 'item' : 'items'} need you
          </Typography>
        </Box>
        <Divider />
        <List dense disablePadding>
          {items.map(item => {
            const meta = ROW_META[item.key];
            const Icon = meta.icon;
            const isUrgent = meta.isUrgent;
            return (
              <ListItemButton
                key={item.key}
                component="a"
                href={getClinicianHref()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleRowClick(item)}
                data-urgency={isUrgent ? 'high' : 'normal'}
                data-notification-type={meta.trackingType}
                sx={{
                  py: 1.25,
                  ...(isUrgent && {
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.error.main, 0.14),
                    },
                  }),
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Icon
                    fontSize="small"
                    sx={{
                      color: isUrgent ? 'error.main' : 'text.secondary',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={meta.buildCopy(item.count)}
                  slotProps={{
                    primary: {
                      variant: 'body2',
                      sx: {
                        fontWeight: isUrgent ? 600 : 400,
                        color: isUrgent ? 'error.dark' : 'text.primary',
                      },
                    },
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Click any item to open in DoseSpot
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
